<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Create a Work Week
      </oxd-text>
      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="workWeek.name"
                :label="namePlacer"
                :rules="rules.name"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />

        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="workWeek.monday"
                type="select"
                :options="dayTypes"
                :rules="rules.monday"
                :label="$t('general.monday')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="workWeek.tuesday"
                type="select"
                :options="dayTypes"
                :rules="rules.tuesday"
                :label="$t('general.tuesday')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="workWeek.wednesday"
                type="select"
                :options="dayTypes"
                :rules="rules.wednesday"
                :label="$t('general.wednesday')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="workWeek.thursday"
                type="select"
                :options="dayTypes"
                :rules="rules.thursday"
                :label="$t('general.thursday')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="workWeek.friday"
                type="select"
                :options="dayTypes"
                :rules="rules.friday"
                :label="$t('general.friday')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="workWeek.saturday"
                type="select"
                :options="dayTypes"
                :rules="rules.saturday"
                :label="$t('general.saturday')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="workWeek.sunday"
                type="select"
                :options="dayTypes"
                :rules="rules.sunday"
                :label="$t('general.sunday')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <work-week-employee-autocomplete
                v-model="workWeek.empNumbers"
                :rules="rules.empNumbers"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  required,
  validSelection,
  validTimeFormat,
  shouldNotExceedCharLength,
  endTimeShouldBeAfterStartTime,
} from '@ohrm/core/util/validation/rules';
import {diffInTime} from '@/core/util/helper/datefns';
import useServerValidation from '@/core/util/composable/useServerValidation';
import WorkWeekEmployeeAutocomplete from '@/orangehrmAdminPlugin/components/WorkWeekEmployeeAutocomplete';

const workWeekModel = {
  monday: null,
  tuesday: null,
  wednesday: null,
  thursday: null,
  friday: null,
  saturday: null,
  sunday: null,
  name: '',
  empNumbers: [],
};
export default {
  components: {
    'work-week-employee-autocomplete': WorkWeekEmployeeAutocomplete,
  },
  props: {
    dayTypes: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/work-week',
    );
    const namePlace = 'Name';
    const {createUniqueValidator} = useServerValidation(http);
    const workWeekUniqueValidation = createUniqueValidator('WorkWeek', 'name');

    return {
      http,
      workWeekUniqueValidation,
    };
  },
  data() {
    return {
      isLoading: false,
      namePlacer: this.namePlace,
      workWeek: {...workWeekModel},
      rules: {
        name: [
          required,
          this.workWeekUniqueValidation,
          shouldNotExceedCharLength(50),
        ],
        monday: [required],
        tuesday: [required],
        wednesday: [required],
        thursday: [required],
        friday: [required],
        saturday: [required],
        sunday: [required],
        empNumbers: [validSelection],
      },
    };
  },
  methods: {
    onSave() {
      this.isLoading = true;
      const payload = {
        name: this.workWeek.name,
        monday: this.workWeek.monday.id,
        tuesday: this.workWeek.tuesday.id,
        wednesday: this.workWeek.wednesday.id,
        thursday: this.workWeek.thursday.id,
        friday: this.workWeek.friday.id,
        saturday: this.workWeek.saturday.id,
        sunday: this.workWeek.sunday.id,
        empNumbers: this.workWeek.empNumbers.map((employee) => employee.id),
      };
      this.http
        .create(payload)
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.workWeek = {...workWeekModel};
          this.onCancel();
        });
    },
    onCancel() {
      navigate('/admin/workWeek');
    },
  },
};
</script>
<style src="./work-shift.scss" lang="scss" scoped></style>
